// MarkdownText — extracted verbatim from App.js.
import { C } from "../lib/theme";

export function MarkdownText({ text, color }) {
  const renderInline = (s, key) => {
    const parts = s.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={`${key}-${i}`} style={{ fontWeight: 700, color: C.text }}>{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith("*") && part.endsWith("*") && part.length > 2) {
        return <em key={`${key}-${i}`}>{part.slice(1, -1)}</em>;
      }
      return part;
    });
  };

  const blocks = text.split(/\n\s*\n/);
  return (
    <div style={{ color: color || C.text }}>
      {blocks.map((block, i) => {
        const trimmed = block.trim();
        if (!trimmed) return null;

        // ## Heading
        if (trimmed.startsWith("## ")) {
          return <h3 key={i} style={{ fontSize: 16, fontWeight: 700, color: C.text, margin: "16px 0 10px", letterSpacing: "-.3px" }}>{renderInline(trimmed.slice(3), i)}</h3>;
        }
        if (trimmed.startsWith("# ")) {
          return <h2 key={i} style={{ fontSize: 18, fontWeight: 800, color: C.text, margin: "16px 0 10px", letterSpacing: "-.4px" }}>{renderInline(trimmed.slice(2), i)}</h2>;
        }
        if (trimmed.startsWith("### ")) {
          return <h4 key={i} style={{ fontSize: 14, fontWeight: 700, color: C.text, margin: "14px 0 8px", letterSpacing: "-.2px" }}>{renderInline(trimmed.slice(4), i)}</h4>;
        }

        const lines = trimmed.split("\n");

        // Markdown table: a header row, a |---|---| separator, then body rows.
        const isTableSep = l => l.includes("|") && l.includes("-") && /^[\s|:-]+$/.test(l);
        if (lines.length >= 2 && lines[0].includes("|") && isTableSep(lines[1])) {
          const splitRow = l => {
            let s = l.trim();
            if (s.startsWith("|")) s = s.slice(1);
            if (s.endsWith("|")) s = s.slice(0, -1);
            return s.split("|").map(c => c.trim());
          };
          const aligns = splitRow(lines[1]).map(c => {
            const L = c.startsWith(":"), R = c.endsWith(":");
            return L && R ? "center" : R ? "right" : "left";
          });
          const header = splitRow(lines[0]);
          const bodyRows = lines.slice(2).filter(l => l.includes("|")).map(splitRow);
          return (
            <div key={i} style={{ overflowX: "auto", margin: "12px 0 16px", border: `1px solid ${C.border2}`, borderRadius: 9, WebkitOverflowScrolling: "touch" }}>
              <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12.5 }}>
                <thead>
                  <tr>
                    {header.map((h, j) => (
                      <th key={j} style={{ textAlign: aligns[j] || "left", padding: "10px 12px", background: C.bg3, color: C.text, fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: ".4px", whiteSpace: "nowrap", borderBottom: `1px solid ${C.border}` }}>{renderInline(h, `${i}-h-${j}`)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bodyRows.map((row, r) => (
                    <tr key={r} style={{ background: r % 2 ? C.bg3 : "#fff" }}>
                      {row.map((cell, j) => (
                        <td key={j} style={{ textAlign: aligns[j] || "left", padding: "8px 12px", color: C.sub, fontSize: 12.5, lineHeight: 1.5, borderBottom: `1px solid ${C.border2}`, verticalAlign: "top" }}>{renderInline(cell, `${i}-${r}-${j}`)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }

        // Numbered list
        if (lines.every(l => /^\d+\.\s/.test(l.trim()))) {
          return (
            <ol key={i} style={{ paddingLeft: 22, margin: "8px 0 12px" }}>
              {lines.map((l, j) => (
                <li key={j} style={{ marginBottom: 6, lineHeight: 1.65 }}>
                  {renderInline(l.replace(/^\d+\.\s/, ""), `${i}-${j}`)}
                </li>
              ))}
            </ol>
          );
        }

        // Bulleted list
        if (lines.every(l => /^[-*]\s/.test(l.trim()))) {
          return (
            <ul key={i} style={{ paddingLeft: 22, margin: "8px 0 12px", listStyle: "disc" }}>
              {lines.map((l, j) => (
                <li key={j} style={{ marginBottom: 6, lineHeight: 1.65 }}>
                  {renderInline(l.replace(/^[-*]\s/, ""), `${i}-${j}`)}
                </li>
              ))}
            </ul>
          );
        }

        // Plain paragraph (with inline line breaks preserved)
        return (
          <p key={i} style={{ margin: "0 0 12px", lineHeight: 1.7, fontSize: 13.5, fontWeight: 500 }}>
            {trimmed.split("\n").map((line, j) => (
              <span key={j}>
                {j > 0 && <br />}
                {renderInline(line, `${i}-${j}`)}
              </span>
            ))}
          </p>
        );
      })}
    </div>
  );
}
